import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { useDarkMode } from '../context/DarkModeContext';

const AcceptInvite: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { darkMode } = useDarkMode();
  const [invitation, setInvitation] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      fetchInvitation();
    } else {
      setError('Invalid invitation link');
      setLoading(false);
    }
  }, [token]);

  const fetchInvitation = async () => {
    if (!token) return;
    try {
      const response = await api.get(`/invitations/${token}`);
      setInvitation(response.data);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Invalid or expired invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!token) {
      setError('Invalid invitation link');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post(`/invitations/${token}/accept`, { password });
      
      // Automatically log in the user
      if (response.data.token && response.data.user) {
        // Store token in localStorage
        localStorage.setItem('token', response.data.token);
        
        // Use the login function which will set the user in AuthContext
        // We'll use the email and password to call the login endpoint
        // This ensures the AuthContext is properly updated
        try {
          await login(response.data.user.email, password);
          // Navigate to dashboard
          navigate('/');
        } catch (loginError) {
          // If login fails, still set the token and user manually
          localStorage.setItem('token', response.data.token);
          // Reload the page to trigger AuthContext to fetch user
          window.location.href = '/';
        }
      } else {
        // If no auto-login, redirect to login page
        navigate('/login');
      }
    } catch (error: any) {
      setError(error.response?.data?.error || 'Error accepting invitation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="max-w-md w-full space-y-8 p-10 bg-white dark:bg-gray-800 rounded-xl shadow-lg">
          <div>
            <h2 className="text-center text-3xl font-extrabold text-gray-900 dark:text-gray-100">
              Invalid Invitation
            </h2>
            <p className="mt-4 text-center text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
            <div className="mt-6 text-center">
              <a
                href="/login"
                className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
              >
                Go to Login
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="max-w-md w-full space-y-8 p-10 bg-white dark:bg-gray-800 rounded-xl shadow-lg">
        <div>
          <h2 className="text-center text-3xl font-extrabold text-gray-900 dark:text-gray-100">
            Accept Invitation
          </h2>
          {invitation?.company_name ? (
            <>
              <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
                You've been invited to join <strong className="text-gray-900 dark:text-gray-100">{invitation.company_name}</strong>
              </p>
              <p className="mt-1 text-center text-sm text-gray-600 dark:text-gray-400">
                Your role: <strong className="text-gray-900 dark:text-gray-100">
                  {invitation.role === 'admin' ? 'Company Admin' : 'Company User'}
                </strong>
              </p>
            </>
          ) : (
            <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
              You've been invited to join the <strong className="text-gray-900 dark:text-gray-100">NxtWorks Portal</strong>
            </p>
          )}
          <p className="mt-1 text-center text-sm text-gray-600 dark:text-gray-400">
            Your email: <strong className="text-gray-900 dark:text-gray-100">{invitation?.email}</strong>
          </p>
          {!invitation?.company_name && (
            <p className="mt-1 text-center text-sm text-gray-600 dark:text-gray-400">
              Your role: <strong className="text-gray-900 dark:text-gray-100">
                {invitation?.role === 'admin' ? 'Global Admin' : 'Global User'}
              </strong>
            </p>
          )}
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
              {error}
            </div>
          )}
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Password (min 6 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="confirm-password" className="sr-only">
                Confirm Password
              </label>
              <input
                id="confirm-password"
                name="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={submitting}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 dark:bg-indigo-500 hover:bg-indigo-700 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Creating Account...' : 'Create Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AcceptInvite;
